import { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useTheme } from 'next-themes';

const GlobalNetwork = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const { resolvedTheme } = useTheme();

    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start end", "end start"]
    });

    const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
    const scale = useTransform(scrollYProgress, [0, 0.5], [0.8, 1]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let width = canvas.width = canvas.clientWidth;
        let height = canvas.height = canvas.clientHeight;

        const nodes: { x: number; y: number; vx: number; vy: number; radius: number }[] = [];
        const nodeCount = 60;
        const connectionDistance = 150;

        // Theme-aware colors
        const isDark = resolvedTheme === 'dark';
        const nodeColor = isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.8)'; // Increased contrast for light mode
        const connectionColor = isDark ? '59, 130, 246' : '37, 99, 235'; // Blueish

        // Initialize nodes
        for (let i = 0; i < nodeCount; i++) {
            nodes.push({
                x: Math.random() * width,
                y: Math.random() * height,
                vx: (Math.random() - 0.5) * 0.5,
                vy: (Math.random() - 0.5) * 0.5,
                radius: Math.random() * 2 + 1,
            });
        }

        let animationFrameId: number;

        const render = () => {
            ctx.clearRect(0, 0, width, height);

            // Update and draw nodes
            nodes.forEach(node => {
                node.x += node.vx;
                node.y += node.vy;

                // Bounce off walls
                if (node.x < 0 || node.x > width) node.vx *= -1;
                if (node.y < 0 || node.y > height) node.vy *= -1;

                // Draw node
                ctx.beginPath();
                ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
                ctx.fillStyle = nodeColor;
                ctx.fill();
            });

            // Draw connections
            ctx.lineWidth = 0.5;
            for (let i = 0; i < nodes.length; i++) {
                for (let j = i + 1; j < nodes.length; j++) {
                    const dx = nodes[i].x - nodes[j].x;
                    const dy = nodes[i].y - nodes[j].y;
                    const distance = Math.sqrt(dx * dx + dy * dy);

                    if (distance < connectionDistance) {
                        ctx.beginPath();
                        ctx.moveTo(nodes[i].x, nodes[i].y);
                        ctx.lineTo(nodes[j].x, nodes[j].y);
                        const alpha = 1 - distance / connectionDistance;
                        ctx.strokeStyle = `rgba(${connectionColor}, ${alpha * 0.5})`;
                        ctx.stroke();
                    }
                }
            }

            animationFrameId = requestAnimationFrame(render);
        };

        render();

        const handleResize = () => {
            width = canvas.width = canvas.clientWidth;
            height = canvas.height = canvas.clientHeight;
        };

        window.addEventListener('resize', handleResize);

        return () => {
            cancelAnimationFrame(animationFrameId);
            window.removeEventListener('resize', handleResize);
        };
    }, [resolvedTheme]);

    return (
        <section ref={containerRef} className="relative h-screen min-h-[800px] w-full bg-background flex items-center justify-center overflow-hidden transition-colors duration-500">
            {/* Background Network Canvas */}
            <canvas
                ref={canvasRef}
                className="absolute inset-0 w-full h-full opacity-50 block"
            />

            <div className="container relative z-10 px-4">
                <motion.div
                    style={{ opacity, scale }}
                    className="max-w-4xl mx-auto text-center"
                >
                    <div className="inline-block mb-4 px-4 py-1.5 rounded-full border border-primary/30 bg-primary/10 backdrop-blur-md">
                        <span className="text-primary text-sm font-semibold tracking-wider uppercase">Global Infrastructure</span>
                    </div>

                    <h2 className="text-5xl md:text-7xl font-bold bg-clip-text text-transparent bg-gradient-to-b from-foreground to-foreground/40 mb-8 tracking-tighter">
                        Connected Everywhere. <br />
                        Instantly.
                    </h2>

                    <p className="text-xl md:text-2xl text-muted-foreground mb-12 max-w-2xl mx-auto leading-relaxed">
                        Our neural logistics network spans across districts and borders, ensuring your cargo finds the most efficient path in real-time.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left mt-16">
                        {[
                            { label: "Active Nodes", value: "1,200+", desc: "Verified collection points" },
                            { label: "Daily Volume", value: "50k", desc: "Transactions processed" },
                            { label: "Latency", value: "<15ms", desc: "Real-time matching speed" }
                        ].map((stat, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 + (i * 0.1) }}
                                viewport={{ once: true }}
                                className="p-6 rounded-2xl bg-foreground/5 border border-foreground/10 hover:bg-foreground/10 transition-colors backdrop-blur-sm"
                            >
                                <h3 className="text-4xl font-bold text-foreground mb-2">{stat.value}</h3>
                                <p className="text-primary font-medium mb-2">{stat.label}</p>
                                <p className="text-sm text-muted-foreground">{stat.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
            </div>

            {/* Overlay Gradients - Adapted for Light/Dark */}
            <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-background to-transparent pointer-events-none" />
            <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-background to-transparent pointer-events-none" />
        </section>
    );
};

export default GlobalNetwork;
